import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {
  Box,
  Button as MUIButton,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api';
import { MoreOptions } from '../Table/assets/MoreOptions';
import DialogContent from '@mui/material/DialogContent';
import TableBody from '@mui/material/TableBody';
import { SPSelectField } from '../Table/fields/SPSelectField';
import { enumItems } from '../../schema/enumItems';
import toast from 'react-hot-toast';

export const SubActivity = ({
  schema,
  values,
  fullWidth,
  forcedRefresh,
}: {
  schema: any;
  values: any;
  fullWidth: boolean;
  forcedRefresh?: () => void;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [selectedSubActivity, setSelectedSubActivity] = useState<any>(
    undefined
  );
  const [subActivities, setSubActivities] = useState<any>([]);

  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = useState<
    boolean
  >(false);

  const fetchMainData = useCallback(() => {
    const searchParams = new URLSearchParams({});

    api
      .get(`/activities/${values?.id}/sub-activities`, searchParams)
      .then((res: any) => {
        setSubActivities(res);
      });
  }, []);

  const handleSubmit = () => {
    const propertiesTemp: any = [...subActivities];
    propertiesTemp.push(selectedSubActivity);

    api
      .patch(`/activities/${values?.id}`, {
        sub_activities: propertiesTemp,
      })
      .then((res) => {
        toast.success(`Successfully added Sub Activity`);

        fetchMainData();
        if (forcedRefresh) {
          forcedRefresh();
        }
        setCurrentAddNewPaneEntity(false);
      })
      .catch((error) => {
        let array: any = [];
        for (let errorKey in error) {
          for (let errorMsg in error[errorKey]) {
            array.push(`${error[errorKey][errorMsg]}`);
          }
        }
        toast.error(array.join(','));
      });
  };

  const handleDelete = (index: number) => {
    const propertiesTemp: any = subActivities.filter(
      (item: any, itemIndex: number) => itemIndex !== index
    );
    api
      .patch(`/activities/${values?.id}`, {
        sub_activities: propertiesTemp,
      })
      .then((res) => {
        toast.success(`Successfully deleted Sub Activity`);
        fetchMainData();
        if (forcedRefresh) {
          forcedRefresh();
        }
        setOpenConfirmDialog(false);
      })
      .catch((error) => {
        let array: any = [];
        for (let errorKey in error) {
          for (let errorMsg in error[errorKey]) {
            array.push(`${error[errorKey][errorMsg]}`);
          }
        }
        toast.error(array.join(','));
      });
  };

  useEffect(() => {
    fetchMainData();
  }, []);

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          mt: '40px',
        }}
      >
        <TableContainer component={Paper}>
          <Table size="small" sx={{}}>
            <TableHead
              sx={{
                '& .MuiTableCell-root': {
                  color: 'inherit',
                  border: 'none',
                },
                '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                  border: 'none',
                  fontSize: '14px',
                  p: '10px',
                },
                background: '#256861',
                color: '#fff',
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    width: '50px',
                  }}
                ></TableCell>
                <TableCell
                  sx={{
                    minWidth: '150px',
                  }}
                >
                  Activity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subActivities.map((subActivity: any, index: number) => {
                return (
                  <TableRow>
                    <TableCell>
                      <MoreOptions
                        hideEdit={true}
                        clickOnEdit={() => {}}
                        clickOnDelete={() => {
                          setOpenConfirmDialog({ id: index });
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgb(0 0 0 / 7%)',
                        },
                      }}
                      onClick={()=>{
                        window.open(
                          `/activities/${subActivity?.id}`,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                    >{subActivity?.name}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction={'row'}>
          <IconButton
            onClick={() => {
              setCurrentAddNewPaneEntity(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
      </Box>

      {currentAddNewPaneEntity && (
        <>
          <Dialog
            open={currentAddNewPaneEntity}
            onClose={() => {
              setCurrentAddNewPaneEntity(false);
            }}
            sx={{
              '& .MuiPaper-root ': {
                width: '100%',
              },
            }}
          >
            <DialogTitle>{'Add new Sub Activity'}</DialogTitle>
            <DialogContent
              sx={{
                padding: '0px',
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    borderLeft: '1px solid #dfdbdb',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f1f1f1',
                    fontSize: {
                      xs: '15px',
                    },
                  }}
                >
                  <span className={'px-[8px]'}>Activity</span>
                </Box>
                <Box
                  sx={{
                    flex: 2,
                    borderBottom: '1px solid #dfdbdb',
                    borderRight: '1px solid #dfdbdb',
                    '& .MuiFormControl-root.MuiTextField-root': {
                      width: '100%',
                    },
                  }}
                >
                  <SPSelectField
                    onChange={(value) => {
                      setSelectedSubActivity(value);
                    }}
                    schema={enumItems.activity}
                    defaultValue={null}
                    forcedValue={null}
                    disabled={false}
                    isMissing={false}
                    accordingToValue={null}
                  />
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <MUIButton
                onClick={() => {
                  setOpenConfirmDialog(false);
                }}
              >
                Cancel
              </MUIButton>
              <MUIButton
                variant={'contained'}
                color={'primary'}
                onClick={() => {
                  handleSubmit();
                }}
              >
                Submit
              </MUIButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={true}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
        >
          <DialogTitle>{'Are you sure you want to delete it?'}</DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                handleDelete(openConfirmDialog.id);
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchMainData();
            setOpenedSlidingPane({});
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
